import { StoreApi, UseBoundStore } from "zustand";

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

/**
 * Zustand utility function which automatically generates selectors for your
 * each property and action in your store and adds them to the store under
 * the `use` property.
 *
 * e.g., instead of...
 * ```ts
 * const bears = useBearStore((state) => state.bears);
 * const increasePopulation = useBearStore((state) => state.increasePopulation);
 * const removeAllBears = useBearStore((state) => state.removeAllBears);
 * ```
 * after wrapping your store with `createStoreSelectors` you can do...
 * ```ts
 * const bears = useBearStore.use.bears();
 * const increasePopulation = useBearStore.use.increasePopulation();
 * const removeAllBears = useBearStore.use.removeAllBears();
 * ```
 *
 * @see https://github.com/pmndrs/zustand/blob/main/docs/guides/auto-generating-selectors.md
 */
export const createStoreSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }

  return store;
};
